import React, { useState, useRef } from "react";
import { contactService } from "../../services/contactService";
import LoadingSpinner from "../common/LoadingSpinner";
import SuccessMessage from "../common/SuccessMessage";
import ErrorMessage from "../common/ErrorMessage";
import { trackContactForm } from "../../services/analyticsService";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  const myForm = useRef(null);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const validation = contactService.validateContactForm(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    
    // Reset previous status
    setSubmitStatus(null);
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await contactService.submitContactForm(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        // Track contact form submission
        trackContactForm();
        // Reset form
        myForm.current.reset();
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setErrors({});
      } else {
        setSubmitStatus('error');
        // Set server-side errors if any
        if (result.errors) {
          setErrors(result.errors);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSubmitStatus(null);
    setErrors({});
  };

  return (
    <div className="contact-box text-center">
      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="mb-4">
          <SuccessMessage 
            message="Thank you for your message! We'll get back to you soon."
            onClose={() => setSubmitStatus(null)}
          />
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="mb-4">
          <ErrorMessage 
            error="Failed to send message. Please try again."
            onRetry={handleRetry}
          />
        </div>
      )}

      <form
        ref={myForm}
        onSubmit={submitHandler}
        className="contact-form"
        noValidate="novalidate"
      >
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                name="name"
                placeholder="Name"
                required="required"
                onChange={changeHandler}
                value={formData.name}
                disabled={isSubmitting}
              />
              {errors.name && (
                <div className="invalid-feedback d-block text-left">
                  {errors.name}
                </div>
              )}
            </div>
            <div className="form-group">
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                name="email"
                placeholder="Email"
                required="required"
                onChange={changeHandler}
                value={formData.email}
                disabled={isSubmitting}
              />
              {errors.email && (
                <div className="invalid-feedback d-block text-left">
                  {errors.email}
                </div>
              )}
            </div>
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                name="subject"
                placeholder="Subject"
                required="required"
                onChange={changeHandler}
                value={formData.subject}
                disabled={isSubmitting}
              />
              {errors.subject && (
                <div className="invalid-feedback d-block text-left">
                  {errors.subject}
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <textarea
                className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                name="message"
                placeholder="Message"
                required="required"
                rows="5"
                onChange={changeHandler}
                value={formData.message}
                disabled={isSubmitting}
              />
              {errors.message && (
                <div className="invalid-feedback d-block text-left">
                  {errors.message}
                </div>
              )}
            </div>
          </div>
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-lg btn-block mt-3"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Sending...</span>
                </>
              ) : (
                <>
                  <span className="text-white pr-3">
                    <i className="fas fa-paper-plane" />
                  </span>
                  Send Message
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;